import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { raccogliDateFilters, handleDateOpChange, resetDateRanges, contaFiltri } from '../lib/date-filter.ts';
import { buildExportQuery, scaricaFile, formatNumero } from '../lib/utils.ts';
import { aggiornaInvioStatini } from './invio-statini.ts';
import { impostaSearchableSelect } from '../lib/searchable-select.ts';
import type { DelegaAltroTerritorioArricchita } from '@/types/entities/delega-altro-territorio.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'territorioNome', label: 'Territorio / cassa edile', type: 'text' },
  { key: 'cognome', label: 'Nominativo', type: 'text' },
  { key: 'codiceFiscale', label: 'Codice Fiscale', type: 'code' },
  { key: 'dataNascita', label: 'Data nascita', type: 'date' },
  { key: 'dataDelega', label: 'Data delega', type: 'date' },
  { key: 'operatore', label: 'Operatore', type: 'text' },
  { key: 'createdAt', label: 'Data registrazione', type: 'date' },
  { key: 'note', label: 'Note', type: 'text' },
  { key: 'statiniInviataAt', label: 'Statini', type: 'text', cssClass: 'text-center' },
  { key: '', label: '', type: 'text' },
];

const PAGE_SIZE = 30;

type OffcanvasInstance = { show(): void; hide(): void; dispose(): void };
type BootstrapOffcanvas = { Offcanvas: new (el: Element) => OffcanvasInstance };

/**
 * wc-tabella-altri-territori — Elenco delle deleghe di altri territori (punto 7).
 *
 * `data-options`: `{ territori: Territorio[] }`; attributi `data-statini-enabled`/`data-statini-staging` per l'invio.
 * Filtri: ricerca rapida, territorio (toolbar, select con ricerca del tema), data registrazione (offcanvas).
 * API pubblica per l'invio a Statini montato in `[data-slot-invio-statini]`:
 *   `filtriCorrenti()` → filtri della lista (stessi parametri di /lista ed /export-csv), `ricarica()`.
 * Evento `altri-territori-caricati` `{ filtri, count }` dopo ogni caricamento.
 */
export class TabellaAltriTerritori extends AppComponent {
  static override componentName = 'wc-tabella-altri-territori';
  protected override get templateId() { return 'tabella-altri-territori'; }

  private dataTable: DataTable | null = null;
  private offcanvas: OffcanvasInstance | null = null;
  private currentPage = 1;
  private currentFilter = '';

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
      statiniEnabled: this.attributeBoolean('data-statini-enabled'),
      statiniStaging: this.attributeBoolean('data-statini-staging'),
    };
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;
    this.setupTableEvents(this.dataTable);
    this.setupFiltri();
    this.delegate.on('click', '[data-action="export-excel"]', (e) => {
      e.preventDefault();
      scaricaFile(`/api/deleghe-altri-territori/export-csv?${buildExportQuery(this.filtriCorrenti())}`);
    });
    this.listen(this, 'statini-inviati', () => this.ricarica());
    this.listen(window, 'message', (e) => {
      const data = (e as MessageEvent).data as { action?: string; refresh?: boolean } | null;
      if (data?.action === 'modal-close' && data.refresh) this.carica();
    });
    this.carica();
  }

  // --- API pubblica ---

  /** Filtri correnti: ricerca rapida, territorio, data registrazione (Da/A) */
  filtriCorrenti(): Record<string, string> {
    const filtri = raccogliDateFilters(this, ['dataRegistrazione']);
    const territorioId = (this.$('[data-filtro-val="territorioId"]') as HTMLSelectElement | null)?.value;
    if (territorioId) filtri.territorioId = territorioId;
    if (this.currentFilter) filtri.q = this.currentFilter;
    return filtri;
  }

  ricarica(): void {
    this.carica();
  }

  // --- Eventi ---

  private setupTableEvents(table: DataTable): void {
    this.listen(table, 'dt-page-change', (e) => {
      this.currentPage = (e as CustomEvent<{ page: number }>).detail.page;
      this.carica();
    });
    this.listen(table, 'dt-filter-change', (e) => {
      this.currentFilter = (e as CustomEvent<{ filter: string }>).detail.filter;
      this.ricaricaDaCapo();
    });
    this.listen(table, 'dt-row-action', (e) => {
      const { action, row } = (e as CustomEvent<{ action: string; row: DelegaAltroTerritorioArricchita }>).detail;
      if (action === 'modifica') this.modifica(row);
      if (action === 'elimina') this.elimina(row);
    });
  }

  private setupFiltri(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    const bootstrap = (globalThis as { bootstrap?: BootstrapOffcanvas }).bootstrap;
    if (offcanvasEl && bootstrap) this.offcanvas = new bootstrap.Offcanvas(offcanvasEl);
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;

    this.delegate.on('click', '[data-action="apri-filtri"]', () => this.offcanvas?.show());
    this.delegate.on('change', '[data-date-op]', (e) => handleDateOpChange(this, e));
    this.delegate.on('change', '[data-filtro-val="territorioId"]', () => this.ricaricaDaCapo());
    if (form) this.listen(form, 'submit', (e) => { e.preventDefault(); this.offcanvas?.hide(); this.ricaricaDaCapo(); });
    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      form?.reset();
      resetDateRanges(this);
      impostaSearchableSelect(this.$('[data-filtro-val="territorioId"]') as HTMLSelectElement | null, '');
      this.offcanvas?.hide();
      this.ricaricaDaCapo();
    });
  }

  // --- Azioni riga ---

  /** La lista si ricarica dal messaggio `modal-close` dell'iframe (come per il nuovo inserimento) */
  private modifica(row: DelegaAltroTerritorioArricchita): void {
    void Weext.modalIframe(`/altri-territori/${row.id}/modifica?layout=modal`, {
      title: 'Modifica delega altro territorio',
      size: 'lg',
    });
  }

  private async elimina(row: DelegaAltroTerritorioArricchita): Promise<void> {
    const giaInviata = row.statiniInviataAt ? ' È già stata inviata a Statini: lì resta.' : '';
    const ok = await Weext.confirm(
      `Eliminare la delega di ${row.cognome} ${row.nome} per ${row.territorioNome}?${giaInviata}`,
      { title: 'Conferma eliminazione', confirmText: 'Elimina', confirmClass: 'btn-danger' },
    );
    if (!ok) return;
    const result = await this.api.delegheAltriTerritori.elimina(row.id);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }
    Weext.toast('Delega eliminata', { type: 'success' });
    this.carica();
  }

  // --- Caricamento ---

  private ricaricaDaCapo(): void {
    this.currentPage = 1;
    this.carica();
  }

  private aggiornaContatori(filtri: Record<string, string>, count: number | null): void {
    const { q: _q, ...avanzati } = filtri;
    const attivi = contaFiltri(avanzati);
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = attivi > 0 ? String(attivi) : '';
      badge.classList.toggle('d-none', attivi === 0);
    }
    this.$('[data-action="reset-filtri-toolbar"]')?.classList.toggle('d-none', attivi === 0);
    const totale = document.querySelector('[data-totale-altri-territori]');
    if (totale) totale.textContent = count === null ? '' : `${formatNumero(count)} deleghe`;
  }

  /** Invio a Statini attivo solo con un giorno o un intervallo di Data registrazione */
  private aggiornaStatini(filtri: Record<string, string>): void {
    const abilitato = !!(filtri.dataRegistrazioneDa || filtri.dataRegistrazioneA);
    const tooltip = abilitato ? 'Invia a Statini le deleghe filtrate' : 'Indica un giorno o un intervallo di Data registrazione';
    aggiornaInvioStatini(this.$('wc-invio-statini'), { filtri, abilitato, tooltip });
  }

  private async carica(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();
    const filtri = this.filtriCorrenti();
    const params = { ...filtri, page: String(this.currentPage), limit: String(PAGE_SIZE) };
    const result = await this.api.delegheAltriTerritori.lista(params);
    if (!result.ok) {
      this.dataTable.setError(result.error);
      this.aggiornaContatori(filtri, null);
      return;
    }
    const { deleghe, count, page, totalPages, pageSize } = result.data;
    this.dataTable.setData(deleghe as unknown as Record<string, unknown>[], { page, totalPages, count, pageSize });
    this.aggiornaContatori(filtri, count);
    this.aggiornaStatini(filtri);
    this.dispatch('altri-territori-caricati', { filtri, count });
  }
}
